/**
 * BOGOS free gift picker: image carousel on each gift card.
 * BOGOS shows one image per gift. This adds the product's other images as slides, with swipe,
 * pagination dots and (on mouse devices) arrows. BOGOS still owns the card: its first image stays
 * in place, so choosing a size still swaps that image.
 *
 * Loaded on theme pages by snippets/bogos-boost-bridge.liquid, and on the Boost collections page
 * by the Tracking Script, which runs the scripts marked data-wayfx-bogos-extra in
 * layout/bogos-embed.liquid. Styles live here so both pages get them without a BOGOS CSS change.
 */
(() => {
  if (window.__wayfxGiftCarousel) return;
  window.__wayfxGiftCarousel = true;

  const POPUP_ID = 'freegifts-main-popup-container';
  const ITEM = '.bogos-offer-gift-item';
  const DONE = 'data-wayfx-gift-carousel';
  const WIDTHS = [300, 600, 900];

  const styles = `
    #${POPUP_ID} .bogos-gift-item-image-container[${DONE}] { isolation: isolate; }
    #${POPUP_ID} .wayfx-gc-track {
      position: absolute; inset: 0; display: flex; overflow-x: auto; overflow-y: hidden;
      scroll-snap-type: x mandatory; overscroll-behavior-x: contain; scrollbar-width: none;
    }
    #${POPUP_ID} .wayfx-gc-track::-webkit-scrollbar { display: none; }
    #${POPUP_ID} .wayfx-gc-slide { position: relative; flex: 0 0 100%; scroll-snap-align: start; scroll-snap-stop: always; }
    #${POPUP_ID} .wayfx-gc-slide > img {
      position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important;
      max-width: none !important; margin: 0 !important; transform: none !important; object-fit: contain;
    }
    #${POPUP_ID} .wayfx-gc-dots {
      position: absolute; left: 50%; bottom: 4px; z-index: 1; display: flex; transform: translateX(-50%);
    }
    #${POPUP_ID} .wayfx-gc-dot {
      display: grid; place-items: center; width: 22px; height: 22px; padding: 0; border: 0;
      background: none; cursor: pointer;
    }
    #${POPUP_ID} .wayfx-gc-dot::before {
      content: ''; width: 7px; height: 7px; border-radius: 50%;
      background: rgba(45, 35, 26, 0.25); transition: background-color 0.2s, transform 0.2s;
    }
    #${POPUP_ID} .wayfx-gc-dot[aria-current='true']::before { background: var(--hs-fg-accent, #994e0e); transform: scale(1.15); }
    #${POPUP_ID} .wayfx-gc-dot:focus-visible { outline: 2px solid var(--hs-fg-accent, #994e0e); outline-offset: -3px; border-radius: 50%; }
    #${POPUP_ID} .wayfx-gc-arrow {
      position: absolute; top: 50%; z-index: 1; display: none; place-items: center;
      width: 32px; height: 32px; padding: 0; margin-top: -16px;
      border: 1px solid var(--hs-fg-border, rgba(45, 35, 26, 0.18)); border-radius: 50%;
      background: var(--hs-fg-card, #fff); color: var(--hs-fg-ink, #2d231a);
      box-shadow: 0 4px 12px rgba(45, 35, 26, 0.16); cursor: pointer; transition: opacity 0.2s;
    }
    #${POPUP_ID} .wayfx-gc-arrow::before {
      content: ''; width: 8px; height: 8px; border: solid currentColor; border-width: 0 0 2px 2px;
      transform: translateX(2px) rotate(45deg);
    }
    #${POPUP_ID} .wayfx-gc-arrow--next::before { transform: translateX(-2px) rotate(-135deg); }
    #${POPUP_ID} .wayfx-gc-arrow--prev { left: 8px; }
    #${POPUP_ID} .wayfx-gc-arrow--next { right: 8px; }
    #${POPUP_ID} .wayfx-gc-arrow:disabled { opacity: 0; pointer-events: none; }
    #${POPUP_ID} .wayfx-gc-arrow:focus-visible { outline: 2px solid var(--hs-fg-accent, #994e0e); outline-offset: 2px; }
    @media (hover: hover) and (pointer: fine) {
      #${POPUP_ID} .wayfx-gc-arrow { display: grid; }
    }
    @media (prefers-reduced-motion: reduce) {
      #${POPUP_ID} .wayfx-gc-dot::before, #${POPUP_ID} .wayfx-gc-arrow { transition: none; }
    }
  `;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // One request per product, shared by every card and every reopen of the picker.
  const imageCache = new Map();
  const productImages = (handle) => {
    if (!imageCache.has(handle)) {
      const request = fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .then((product) => (product?.media ?? []).filter((media) => media.media_type === 'image'))
        .catch(() => []);
      imageCache.set(handle, request);
    }
    return imageCache.get(handle);
  };

  const fileName = (src) => (src ?? '').split('?')[0].split('/').pop();

  const sizedUrl = (src, width) => {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);
    return url.href;
  };

  const button = (className, label) => {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = className;
    el.setAttribute('aria-label', label);
    return el;
  };

  const enhance = async (item) => {
    const container = item.querySelector('.bogos-gift-item-image-container');
    const link = container?.querySelector('a.gift-original-url');
    const firstImage = link?.querySelector('img.bogos-gift-item-image');
    if (!firstImage || container.hasAttribute(DONE)) return;
    container.setAttribute(DONE, '');

    const handle = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return;
    // BOGOS already shows the selected size's image; add the product images it doesn't show.
    const shown = fileName(firstImage.getAttribute('src'));
    const extra = (await productImages(handle)).filter((media) => fileName(media.src) !== shown);
    if (!extra.length || !item.isConnected) return;

    const total = extra.length + 1;
    const alt = firstImage.alt;
    const track = document.createElement('div');
    track.className = 'wayfx-gc-track';
    track.setAttribute('role', 'group');
    track.setAttribute('aria-roledescription', 'carousel');
    track.setAttribute('aria-label', `${alt} images`);

    const slide = (image, index) => {
      const el = document.createElement('div');
      el.className = 'wayfx-gc-slide';
      el.setAttribute('role', 'group');
      el.setAttribute('aria-roledescription', 'slide');
      el.setAttribute('aria-label', `${index + 1} of ${total}`);
      el.append(image);
      return el;
    };

    track.append(slide(firstImage, 0));
    extra.forEach((media, i) => {
      const img = document.createElement('img');
      img.src = sizedUrl(media.src, 600);
      img.srcset = WIDTHS.map((width) => `${sizedUrl(media.src, width)} ${width}w`).join(', ');
      img.sizes = '(max-width: 749px) 80vw, 300px';
      img.alt = media.alt || alt;
      img.loading = 'lazy';
      img.decoding = 'async';
      track.append(slide(img, i + 1));
    });
    link.append(track);

    const prev = button('wayfx-gc-arrow wayfx-gc-arrow--prev', 'Previous image');
    const next = button('wayfx-gc-arrow wayfx-gc-arrow--next', 'Next image');
    const dots = document.createElement('div');
    dots.className = 'wayfx-gc-dots';
    const dotButtons = Array.from({ length: total }, (_, i) => {
      const dot = button('wayfx-gc-dot', `Show image ${i + 1} of ${total}`);
      dot.addEventListener('click', () => goTo(i));
      dots.append(dot);
      return dot;
    });
    container.append(prev, next, dots);

    let current = -1;
    const update = () => {
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      if (index === current) return;
      current = index;
      dotButtons.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      prev.disabled = index === 0;
      next.disabled = index === total - 1;
    };
    const goTo = (index) => {
      const target = Math.max(0, Math.min(total - 1, index));
      track.scrollTo({ left: target * track.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    let frame = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    }, { passive: true });
    prev.addEventListener('click', () => goTo(current - 1));
    next.addEventListener('click', () => goTo(current + 1));
    // A size choice swaps the first image, so bring it into view.
    item.addEventListener('click', (event) => {
      if (event.target.closest('.bogos-gift-select-variant-option')) goTo(0);
    });
    update();
  };

  const enhanceAll = (popup) => popup.querySelectorAll(ITEM).forEach((item) => enhance(item));

  // BOGOS builds the picker after load and re-renders its cards; enhance each new card once.
  // A timer, not an animation frame: frames pause in background tabs, which left the queue stuck.
  const watch = (popup) => {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        enhanceAll(popup);
      }, 0);
    }).observe(popup, { childList: true, subtree: true });
    enhanceAll(popup);
  };

  let watching = false;
  const findPopup = () => {
    if (watching) return true;
    const popup = document.getElementById(POPUP_ID);
    if (popup) {
      watching = true;
      watch(popup);
    }
    return watching;
  };

  const start = () => {
    const style = document.createElement('style');
    style.textContent = styles;
    document.head.append(style);

    // BOGOS opening its picker later is caught by its own event; the page-wide watch stops early.
    document.addEventListener('fg-gifts:show-slider', () => setTimeout(findPopup, 0));
    if (findPopup()) return;
    const finder = new MutationObserver(() => findPopup() && finder.disconnect());
    finder.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => finder.disconnect(), 30000);
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
